import { useCallback, useEffect, useId, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { Button, Label, Modal, ModalFooter } from "@/components/ui";
import { getCroppedImageFile } from "@/lib/cropImage";

/**
 * Modal uses `animate-scale-in` (transform: scale). react-easy-crop sizes the
 * crop area from getBoundingClientRect(), which is wrong while the dialog is
 * still scaling. Blob previews load fast enough to hit that window. Wait for
 * the animation to finish before mounting the cropper.
 */
const MODAL_SCALE_IN_MS = 220;

export interface ImageCropSession {
  imageSrc: string;
  filename: string;
}

export interface ImageCropModalLabels {
  title: string;
  description: string;
  confirm: string;
  cancel: string;
  zoom: string;
  failed: string;
}

export interface ImageCropModalProps {
  session: ImageCropSession | null;
  /** Crop frame width / height (e.g. 16/9). */
  aspect: number;
  labels: ImageCropModalLabels;
  onConfirm: (file: File) => void;
  onCancel: () => void;
  /** Optional CSS class on the crop hole (e.g. storefront 4:3 guide). */
  cropAreaClassName?: string;
}

export function ImageCropModal({
  session,
  aspect,
  labels,
  onConfirm,
  onCancel,
  cropAreaClassName,
}: ImageCropModalProps) {
  const { t: tCommon } = useTranslation("common");
  const zoomId = useId();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [cropperReady, setCropperReady] = useState(false);

  const imageSrc = session?.imageSrc;
  const filename = session?.filename ?? "image.jpg";
  const isOpen = session !== null;

  useEffect(() => {
    if (!isOpen || !imageSrc) {
      setCropperReady(false);
      return;
    }
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setIsConfirming(false);
    setCropperReady(false);
    const timer = window.setTimeout(() => setCropperReady(true), MODAL_SCALE_IN_MS);
    return () => {
      window.clearTimeout(timer);
      setCropperReady(false);
    };
  }, [isOpen, imageSrc]);

  const handleConfirm = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels || isConfirming) {
      return;
    }
    setIsConfirming(true);
    try {
      const file = await getCroppedImageFile(
        imageSrc,
        croppedAreaPixels,
        filename,
      );
      onConfirm(file);
    } catch (error) {
      console.error("Crop failed:", error);
      toast.error(labels.failed);
      setIsConfirming(false);
    }
  }, [
    imageSrc,
    croppedAreaPixels,
    filename,
    isConfirming,
    onConfirm,
    labels.failed,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      closeLabel={tCommon("close")}
      title={labels.title}
      description={labels.description}
      size="xl"
      dismissOnBackdrop={false}
      closeOnEscape
      footer={
        <ModalFooter>
          <Button variant="secondary" onClick={onCancel} disabled={isConfirming}>
            {labels.cancel}
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            isLoading={isConfirming}
            disabled={!croppedAreaPixels}
          >
            {labels.confirm}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        <div className="relative h-[min(55dvh,28rem)] w-full overflow-hidden rounded-xl bg-neutral-900">
          {cropperReady && imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_: Area, pixels: Area) => {
                setCroppedAreaPixels(pixels);
              }}
              showGrid={false}
              classes={{
                containerClassName: "rounded-xl",
                cropAreaClassName,
              }}
              style={{
                cropAreaStyle: {
                  // No border: border-box would shrink the visible hole vs crop math.
                  border: "0",
                  outline: "2px solid rgba(255, 255, 255, 0.95)",
                  color: "rgba(0, 0, 0, 0.45)",
                },
              }}
            />
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor={zoomId} size="sm">
            {labels.zoom}
          </Label>
          <input
            id={zoomId}
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            className="w-full accent-primary-600"
            disabled={isConfirming || !cropperReady}
          />
        </div>
      </div>
    </Modal>
  );
}
